/**
 * Cytoscape configuration objects for DependencyNetworkGraph
 */

import { getHealthColor } from './cytoscapeTransform';

/**
 * Get node color based on node type and direction
 * @param {Object} nodeData - Node data object
 * @returns {string} Hex color code
 */
export const getNodeColor = (nodeData) => {
  if (nodeData.isRoot) return '#00e5ff'; // cyan for main changes
  if (nodeData.direction === 'upstream') return '#b24bf3'; // purple
  if (nodeData.direction === 'downstream') return '#ff6b35'; // orange
  return '#6b7280'; // gray
};

/**
 * Cytoscape style configuration
 */
export const cytoscapeStyle = [
  {
    selector: 'node',
    style: {
      'background-color': (ele) => getNodeColor(ele.data()),
      'border-width': 4,
      'border-color': (ele) => getHealthColor(ele.data('healthScore') || 0),
      'label': 'data(label)',
      'color': '#ffffff',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': '10px',
      'font-weight': (ele) => ele.data('isRoot') ? 'bold' : 'normal',
      'width': (ele) => ele.data('isRoot') ? 70 : 50,
      'height': (ele) => ele.data('isRoot') ? 70 : 50,
      'text-wrap': 'wrap',
      'text-max-width': '60px',
      'overlay-opacity': 0,
      'transition-property': 'background-color, border-color, border-width',
      'transition-duration': '0.3s'
    }
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 6,
      'border-color': '#ffffff',
      'overlay-opacity': 0
    }
  },
  {
    selector: 'edge',
    style: {
      'width': 2,
      'line-color': (ele) => {
        if (ele.data('direction') === 'upstream') return '#b24bf3';
        if (ele.data('direction') === 'downstream') return '#ff6b35';
        return '#6b7280';
      },
      'target-arrow-color': (ele) => {
        if (ele.data('direction') === 'upstream') return '#b24bf3';
        if (ele.data('direction') === 'downstream') return '#ff6b35';
        return '#6b7280';
      },
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'opacity': 0.6,
      'transition-property': 'line-color, width, opacity',
      'transition-duration': '0.3s'
    }
  },
  {
    selector: 'edge:selected',
    style: {
      'width': 4,
      'opacity': 1
    }
  }
];

/**
 * Cytoscape layout configuration using cose-bilkent algorithm
 */
export const layoutConfig = {
  name: 'cose-bilkent',
  quality: 'proof',
  nodeDimensionsIncludeLabels: true,
  animate: true,
  animationDuration: 1000,
  animationEasing: 'ease-out',
  randomize: false,
  idealEdgeLength: 120,
  nodeRepulsion: 6000,
  edgeElasticity: 0.45,
  nestingFactor: 0.1,
  gravity: 0.25,
  numIter: 2500,
  tile: true,
  tilingPaddingVertical: 10,
  tilingPaddingHorizontal: 10
};

/**
 * Create Cytoscape instance configuration
 * @param {HTMLElement} container - DOM container element
 * @param {Array} nodes - Array of node objects
 * @param {Array} edges - Array of edge objects
 * @returns {Object} Cytoscape configuration object
 */
export const createCytoscapeConfig = (container, nodes, edges) => {
  return {
    container,
    elements: [...nodes, ...edges],
    style: cytoscapeStyle,
    layout: layoutConfig,
    minZoom: 0.3,
    maxZoom: 3,
    wheelSensitivity: 0.2
  };
};
